import { MdLuggage, MdAirlineSeatReclineNormal } from "react-icons/md";
import { TbToolsKitchen2 } from "react-icons/tb";
import i18n from "src/i18n";
import type { BaggageAddOn, FlightOrderDetails, MealAddOn, SeatAddOn } from "src/store/slices/client/client.api";
import {
  AddOnSummaryRow,
  AggregatedBaggage,
  AggregatedMeal,
  AggregatedSeat,
  FlightRef,
  SSRFlight,
  TransformedSegment,
  TabKey,
  DisplayItem,
} from "../constants";

export function aggregateByCode<T extends { code: string; price: number }>(items: T[]): (T & { quantity: number; totalPrice: number })[] {
  const map = new Map<string, T & { quantity: number; totalPrice: number }>();
  for (const item of items) {
    const existing = map.get(item.code);
    if (existing) {
      existing.quantity += 1;
      existing.totalPrice += item.price;
    } else {
      map.set(item.code, { ...item, quantity: 1, totalPrice: item.price });
    }
  }
  return Array.from(map.values());
}

export function transformFlightOrderDetails(data: FlightOrderDetails): TransformedSegment[] {
  return data.segments.map((segment) => {
    const meals: SSRFlight<AggregatedMeal>[] = [];
    const baggages: SSRFlight<AggregatedBaggage>[] = [];
    const seats: SSRFlight<AggregatedSeat>[] = [];

    const airportRefs = new Map<string, FlightRef["origin"]>();
    for (const f of segment.flights) {
      airportRefs.set(f.origin.code, f.origin);
      airportRefs.set(f.destination.code, f.destination);
    }

    for (const flight of segment.flights) {
      const rawMeals: MealAddOn[] = [];
      const rawSeats: SeatAddOn[] = [];

      for (const passenger of data.passengers) {
        for (const m of passenger.meal ?? []) {
          if (m.flightNumber === flight.flightNumber && m.origin === flight.origin.code && m.destination === flight.destination.code) {
            rawMeals.push(m);
          }
        }
        for (const s of passenger.seat ?? []) {
          if (s.flightNumber === flight.flightNumber && s.origin === flight.origin.code && s.destination === flight.destination.code) {
            rawSeats.push(s);
          }
        }
      }

      const flightRef: FlightRef = { flightNumber: flight.flightNumber, origin: flight.origin, destination: flight.destination };
      if (rawMeals.length > 0) meals.push({ flight: flightRef, items: aggregateByCode(rawMeals) });
      if (rawSeats.length > 0) seats.push({ flight: flightRef, items: aggregateByCode(rawSeats) });
    }

    const baggageGroups = new Map<string, { flightRef: FlightRef; items: BaggageAddOn[] }>();
    for (const passenger of data.passengers) {
      for (const b of passenger.baggage ?? []) {
        const originRef = airportRefs.get(b.origin);
        const destRef = airportRefs.get(b.destination);
        if (!originRef || !destRef) continue;
        const key = `${b.origin}|${b.destination}`;
        if (!baggageGroups.has(key)) {
          baggageGroups.set(key, { flightRef: { flightNumber: b.flightNumber, origin: originRef, destination: destRef }, items: [] });
        }
        baggageGroups.get(key)!.items.push(b);
      }
    }
    for (const { flightRef, items } of baggageGroups.values()) {
      baggages.push({ flight: flightRef, items: aggregateByCode(items) });
    }

    const { flights: _flights, ...rest } = segment;
    return { ...rest, meals, baggages, seats };
  });
}

export const getMealLabel = (meal: { code: string; airlineDescription: string | null }) =>
  meal.code === "NoMeal" ? i18n.t("UserHistory.flight.noMeal") : meal.airlineDescription ?? meal.code;

export const getBaggageLabel = (baggage: { code: string; weight: number }) =>
  baggage.code === "NoBaggage" ? i18n.t("UserHistory.flight.noBaggage") : i18n.t("UserHistory.flight.weightKg", { weight: baggage.weight });

export function toDisplayItems(tab: TabKey, ssrFlight: SSRFlight<AggregatedMeal | AggregatedBaggage | AggregatedSeat>): DisplayItem[] {
  if (tab === "meals") {
    return (ssrFlight as SSRFlight<AggregatedMeal>).items.map((m) => ({
      label: getMealLabel(m),
      quantity: m.quantity,
      price: m.price,
      currency: m.currency,
    }));
  }
  if (tab === "baggages") {
    return (ssrFlight as SSRFlight<AggregatedBaggage>).items.map((b) => ({
      label: getBaggageLabel(b),
      quantity: b.quantity,
      price: b.price,
      currency: b.currency,
    }));
  }
  return (ssrFlight as SSRFlight<AggregatedSeat>).items.map((s) => ({
    label: s.seatNo ? i18n.t("UserHistory.flight.seatNumber", { seatNo: s.seatNo }) : s.code,
    quantity: s.quantity,
    price: s.price,
    currency: s.currency,
  }));
}

export function hasAnySSR(segments: TransformedSegment[]): boolean {
  return segments.some(
    (seg) => seg.meals.length > 0 || seg.baggages.length > 0 || seg.seats.length > 0,
  );
}

export function buildAddOnSummaryFromSegments(segments?: TransformedSegment[]): AddOnSummaryRow[] {
  let mealTotal = 0,
    seatTotal = 0,
    baggageTotal = 0;
  let hasMeal = false,
    hasSeat = false,
    hasBaggage = false;
  let currency = "";

  for (const seg of segments ?? []) {
    for (const sf of seg.meals) {
      for (const item of sf.items) {
        mealTotal += item.totalPrice;
        currency = item.currency;
        hasMeal = true;
      }
    }
    for (const sf of seg.seats) {
      for (const item of sf.items) {
        seatTotal += item.totalPrice;
        currency = item.currency;
        hasSeat = true;
      }
    }
    for (const sf of seg.baggages) {
      for (const item of sf.items) {
        baggageTotal += item.totalPrice;
        currency = item.currency;
        hasBaggage = true;
      }
    }
  }

  const rows: AddOnSummaryRow[] = [];
  if (hasMeal) rows.push({ icon: <TbToolsKitchen2 size={16} />, label: i18n.t("UserHistory.flight.meal"), total: mealTotal, currency });
  if (hasSeat) rows.push({ icon: <MdAirlineSeatReclineNormal size={16} />, label: i18n.t("UserHistory.flight.seat"), total: seatTotal, currency });
  if (hasBaggage) rows.push({ icon: <MdLuggage size={16} />, label: i18n.t("UserHistory.flight.baggage"), total: baggageTotal, currency });
  return rows;
}
